// hooks/useGatePassForm.ts - Custom hook for Gate Pass Form state management

import { useState, useRef, useCallback, useEffect } from 'react';

// Types
import type { 
  FormData, 
  UserInfo, 
  ApproverSelection, 
  ReviewerSelection, 
  Gate, 
  Personnel, 
  Region, 
  StatusMessage,
  GatePassRequest,
  SubmissionSuccessData
} from '../types/GatePassTypes';

// Utils
import { 
  createDefaultFormData, 
  mergeFormData, 
  adaptGateForForm,
  validateDateRange,
  //getExpiredPersonnel,
  createErrorMessage,
  generateSubmissionDateString,
  getRegionName
} from '../utils/GatePassUtils';

// Constants
import { 
  STEPS, 
  API_BASE_URL, 
  FALLBACK_REGIONS, 
  AUTO_SAVE_INTERVAL_MS, 
  PROCESSING_TIME 
} from '../constants/GatePassConstants';

// Services
import { saveDraft, loadDraft } from '../draftService';

// Inline submitDraft helper - calls the draft submit API endpoint
// This keeps the hook self-contained and avoids export issues with draftService
const submitDraftToApi = async (draftId: number, getAccessToken: () => Promise<string>) => {
  const API_URL = process.env.REACT_APP_API_URL || 'https://gatepass-api-1753632520.azurewebsites.net/api';
  const accessToken = await getAccessToken();
  const authHeader = accessToken.startsWith('Bearer ') ? accessToken : `Bearer ${accessToken}`;
  
  const response = await fetch(`${API_URL}/submitDraft2/${draftId}`, {
    method: 'POST',
    headers: {
      'Authorization': authHeader,
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    }
  });

  if (!response.ok) {
    if (response.status === 404) throw new Error('Draft not found or access denied');
    if (response.status === 400) {
      const result = await response.json();
      // Extract missing fields from completionStatus to give user actionable feedback
      if (result.completionStatus) {
        const missing: string[] = [];
        if (!result.completionStatus.basicInfo) missing.push('Basic Info (Name, Email, Company, ID Details, Region)');
        if (!result.completionStatus.requestDetails) missing.push('Request Details (Consignment, Request Type, Pass Type)');
        if (!result.completionStatus.approvers) missing.push('Approvers');
        if (!result.completionStatus.gates) missing.push('Gates');
        if (!result.completionStatus.personnel) missing.push('Personnel');
        if (missing.length > 0) {
          throw new Error(`Draft is incomplete. Missing: ${missing.join('; ')}`);
        }
      }
      throw new Error(result.error || 'Draft is incomplete and cannot be submitted');
    }
    const errorText = await response.text();
    throw new Error(`HTTP ${response.status}: ${errorText || 'Failed to submit draft'}`);
  }

  const result = await response.json();
  if (!result.success) {
    throw new Error(result.message || result.error || 'Failed to submit draft');
  }
  return result;
};

/**
 * Check if a nationality is Qatari (case-insensitive).
 * Qatari nationals are exempt from visa requirements.
 */
const isQatariNational = (nationality: string | undefined | null): boolean => {
  if (!nationality) return false;
  const normalized = nationality.trim().toLowerCase();
  return normalized === 'qatar' || normalized === 'qatari' || normalized === 'qa';
};

/**
 * Get personnel who have document issues (no/expired visa or expired passport),
 * excluding Qatari nationals who are exempt from visa requirements.
 */
const getPersonnelWithVisaIssues = (personnel: Personnel[]): Personnel[] => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return personnel.filter(person => {
    const personAny = person as any;
    const passportExpiry = personAny.passportExpiryDate || personAny.passportExpiry;

    // Check passport expiry (applies to ALL nationalities)
    if (passportExpiry) {
      const passportDate = new Date(passportExpiry);
      passportDate.setHours(0, 0, 0, 0);
      if (passportDate < today) {
        return true;
      }
    }

    // Qatari nationals are exempt from visa requirements
    if (isQatariNational(person.nationality)) {
      return false;
    }

    // No visa expiry date means no visa
    if (!person.visaExpiry) {
      return true;
    }

    // Check if visa is expired
    const visaExpiryDate = new Date(person.visaExpiry);
    visaExpiryDate.setHours(0, 0, 0, 0);
    return visaExpiryDate < today;
  });
};

// Hook Props Interface
interface UseGatePassFormProps {
  getUserInfo: () => UserInfo;
  getCurrentUserId: () => string | null;
  getAccessToken: () => Promise<string>;
  onStatusUpdate: (status: StatusMessage) => void;
  onSubmissionSuccess: (successData: SubmissionSuccessData) => void;
  initialDraftId?: number | null;
  onDraftSaved?: (draftId: number) => void;
  onNavigateToSavedDrafts?: () => void;
}

export const useGatePassForm = ({
  getUserInfo,
  getCurrentUserId,
  getAccessToken,
  onStatusUpdate,
  onSubmissionSuccess,
  initialDraftId = null,
  onDraftSaved,
  onNavigateToSavedDrafts
}: UseGatePassFormProps) => {

  // ============================================
  // REFS AND INITIALIZATION
  // ============================================
  
  // Refs to prevent infinite loops
  const isInitializedRef = useRef(false);
  const isLoadingRegionsRef = useRef(false);
  const isLoadingDraftRef = useRef(false);

  // User info and default form data
  // NOTE: createDefaultFormData is called on every render so we must NOT reference
  // defaultFormData directly inside useState — use a lazy initializer instead.
  const userInfo = getUserInfo();
  const defaultFormData = createDefaultFormData(userInfo);

  // Capture the cleared initial state once via ref so re-renders never overwrite it
  const initialFormDataRef = useRef<FormData | null>(null);
  if (!initialFormDataRef.current) {
    initialFormDataRef.current = {
      ...createDefaultFormData(userInfo),
      passType: '' as FormData['passType'],
      startDate: '',
      endDate: '',
    };
  }

  // ============================================
  // FORM STATE
  // ============================================
  
  // Navigation
  const [currentStep, setCurrentStep] = useState(1);
  
  // Form data — initialFormDataRef ensures the cleared values are only set once
  // regardless of how many times the hook re-renders before mount completes
  const [formData, setFormData] = useState<FormData>(() => initialFormDataRef.current!);
  
  // Draft management
  const [currentDraftId, setCurrentDraftId] = useState<number | null>(initialDraftId);
  const [originalRequestId, setOriginalRequestId] = useState<number | null>(null);
  const [showSaveDraftDialog, setShowSaveDraftDialog] = useState<boolean>(false);
  const [isSavingDraft, setIsSavingDraft] = useState<boolean>(false);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [isLoadingDraft, setIsLoadingDraft] = useState<boolean>(false);
  const [draftLoadError, setDraftLoadError] = useState<string>('');

  // ============================================
  // REGIONS STATE
  // ============================================
  
  const [regions, setRegions] = useState<Region[]>([]);
  const [loadingRegions, setLoadingRegions] = useState<boolean>(false);
  const [regionError, setRegionError] = useState<string>('');

  // ============================================
  // SELECTIONS STATE
  // ============================================
  
  const [selectedGates, setSelectedGates] = useState<Gate[]>([]);
  const [selectedPersonnel, setSelectedPersonnel] = useState<Personnel[]>([]);
  const [personnelVisaIssues, setPersonnelVisaIssues] = useState<Personnel[]>([]);
  const [approverSelection, setApproverSelection] = useState<ApproverSelection | null>(null);
  const [reviewerSelection, setReviewerSelection] = useState<ReviewerSelection | null>(null);
  
  // Parent request loaded from draft (for supplementary requests)
  const [loadedParentRequest, setLoadedParentRequest] = useState<any>(null);

  // ============================================
  // POPUP STATE
  // ============================================
  
  const [showGateSelection, setShowGateSelection] = useState<boolean>(false);
  const [showPersonnelSelection, setShowPersonnelSelection] = useState<boolean>(false);
  const [showApproverSelection, setShowApproverSelection] = useState<boolean>(false);
  const [showReviewerSelection, setShowReviewerSelection] = useState<boolean>(false);

  // ============================================
  // FILE AND SUBMISSION STATE
  // ============================================
  
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  // ============================================
  // API FUNCTIONS
  // ============================================

  // Fetch regions
  const fetchRegions = useCallback(async (): Promise<void> => {
    if (isLoadingRegionsRef.current || regions.length > 0) {
      return;
    }

    try {
      isLoadingRegionsRef.current = true;
      setLoadingRegions(true);
      setRegionError('');
      
      const accessToken = await getAccessToken();
      const response = await fetch(`${API_BASE_URL}/regions`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch regions: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.success && result.data) {
        setRegions(result.data);
      } else {
        throw new Error(result.message || 'Failed to load regions');
      }
    } catch (error) {
      console.error('Error fetching regions:', error);
      setRegionError('Failed to load regions. Please refresh the page.');
      setRegions(FALLBACK_REGIONS);
    } finally {
      setLoadingRegions(false);
      isLoadingRegionsRef.current = false;
    }
  }, [getAccessToken, regions.length]);

  // Load existing draft
  const loadExistingDraft = useCallback(async (draftId: number): Promise<void> => {
    if (isLoadingDraftRef.current) {
      return;
    }

    try {
      isLoadingDraftRef.current = true;
      setIsLoadingDraft(true);
      setDraftLoadError('');
      
      const draft = await loadDraft(draftId, getAccessToken);
      
      if (draft && draft.formData) {
        const safeFormData = mergeFormData(defaultFormData, draft.formData);
        
        // ═══════════════════════════════════════════════════════════════
        // mergeFormData only copies keys present in defaultFormData.
        // parentRequestId is NOT a default field — it's set dynamically
        // when the user picks a parent request for supplementary type.
        // We must explicitly preserve it or it gets silently dropped.
        // ═══════════════════════════════════════════════════════════════
        if (draft.formData.parentRequestId) {
          (safeFormData as any).parentRequestId = draft.formData.parentRequestId;
          console.log('✅ parentRequestId preserved after mergeFormData:', draft.formData.parentRequestId);
        }
        
        setFormData(safeFormData);
        setSelectedGates(draft.selectedGates || draft.gates || []);
        setSelectedPersonnel(draft.selectedPersonnel || draft.personnel || []);
        setCurrentDraftId((draft as any).draftId || draftId);
        
        // ═══════════════════════════════════════════════════════════════
        // Reconstruct approverSelection from draft formData
        // The draft stores flat fields like approverEmail, approverUserId
        // but Step3 reads from the approverSelection object.
        // ═══════════════════════════════════════════════════════════════
        const fd = draft.formData;
        if (fd.approverEmail && fd.approverUserId) {
          const emails = fd.approverEmail.split(',').map((e: string) => e.trim()).filter(Boolean);
          const userIds = fd.approverUserId.split(',').map((u: string) => u.trim()).filter(Boolean);
          const names = fd.approverNames ? fd.approverNames.split(',').map((n: string) => n.trim()) : [];
          
          if (emails.length > 0 && userIds.length > 0) {
            const approvers = emails.map((email: string, i: number) => ({
              userId: parseInt(userIds[i]) || 0,
              userName: names[i] || email.split('@')[0] || email,
              userEmail: email
            }));
            
            setApproverSelection({
              function: fd.selectedFunctionId 
                ? { functionId: fd.selectedFunctionId, functionName: fd.approverFunctionName || '', approvers: [], reviewers: [] } as any
                : null,
              selectedApprovers: approvers
            });
            console.log('✅ Approver selection reconstructed from draft:', approvers.length, 'approvers');
          }
        }
        
        // Reconstruct reviewerSelection from draft formData
        if (fd.reviewerEmail && fd.reviewerUserId) {
          const rEmails = fd.reviewerEmail.split(',').map((e: string) => e.trim()).filter(Boolean);
          const rUserIds = fd.reviewerUserId.split(',').map((u: string) => u.trim()).filter(Boolean);
          const rNames = fd.reviewerNames ? fd.reviewerNames.split(',').map((n: string) => n.trim()) : [];
          
          if (rEmails.length > 0 && rUserIds.length > 0) {
            const reviewers = rEmails.map((email: string, i: number) => ({
              userId: parseInt(rUserIds[i]) || 0,
              userName: rNames[i] || email.split('@')[0] || email,
              userEmail: email
            }));
            
            setReviewerSelection({
              company: fd.selectedCompanyId 
                ? { companyId: fd.selectedCompanyId, companyName: fd.reviewerCompanyName || '' } as any 
                : null,
              function: (fd as any).selectedReviewerFunctionId
                ? { functionId: (fd as any).selectedReviewerFunctionId, functionName: (fd as any).reviewerFunctionName || '', approvers: [], reviewers: [] } as any
                : null,
              selectedReviewers: reviewers
            });
            console.log('✅ Reviewer selection reconstructed from draft:', reviewers.length, 'reviewers');
          }
        }
        
        // Track originalRequestId for revision drafts
        // Read from top-level (returned by loadDraft API) OR from formData as fallback
        const draftOriginalRequestId = (draft as any).originalRequestId 
          || fd.originalRequestId 
          || null;
        if (draftOriginalRequestId) {
          setOriginalRequestId(draftOriginalRequestId);
          console.log('🔄 Revision draft detected, originalRequestId:', draftOriginalRequestId);
        }
        
        // ═══════════════════════════════════════════════════════════════
        // Reconstruct parentRequest for supplementary drafts
        // The API returns draft.parentRequest with full parent details
        // ═══════════════════════════════════════════════════════════════
        if ((draft as any).parentRequest) {
          setLoadedParentRequest((draft as any).parentRequest);
          console.log('✅ Parent request restored from draft:', (draft as any).parentRequest.id);
        } else if (fd.parentRequestId && fd.requestType === 'supplementary') {
          // Fallback: if parentRequest object wasn't returned but we have the ID,
          // set a minimal object so the UI shows something
          setLoadedParentRequest({
            id: fd.parentRequestId,
            name: '',
            companyName: '',
            contractNumber: '',
            startDate: '',
            endDate: '',
            submittedAt: ''
          });
          console.log('⚠️ Parent request ID found but no details, using minimal fallback:', fd.parentRequestId);
        }
        
        onStatusUpdate({
          type: 'success',
          message: draftOriginalRequestId 
            ? `Revision draft for Request #${draftOriginalRequestId} loaded successfully`
            : `Draft "${draft.draftName}" loaded successfully`
        });
      } else {
        setDraftLoadError('Draft data is incomplete');
        onStatusUpdate({
          type: 'error',
          message: 'Draft loaded but some data is missing'
        });
      }
    } catch (error) {
      console.error('Error loading draft:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      setDraftLoadError(errorMessage);
      onStatusUpdate({
        type: 'error',
        message: `Failed to load draft: ${errorMessage}`
      });
    } finally {
      setIsLoadingDraft(false);
      isLoadingDraftRef.current = false;
    }
  }, [getAccessToken, onStatusUpdate, defaultFormData]);

  // Submit gate pass request without file
  const submitGatePassRequest = useCallback(async (
    requestData: GatePassRequest
  ): Promise<{
    success: boolean;
    requestId?: string;
    submissionDate?: string;
    estimatedProcessingTime?: string;
    approvalWorkflowStarted?: boolean;
    message?: string;
    data?: any;
  }> => {
    try {
      const accessToken = await getAccessToken();
      const response = await fetch(`${API_BASE_URL}/gatepass`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify(requestData)
      });

      const result = await response.json();

      if (response.ok) {
        return {
          success: true,
          requestId: result.requestId || result.data?.id || `REQ-${Date.now()}`,
          submissionDate: result.submissionDate || new Date().toISOString(),
          estimatedProcessingTime: result.estimatedProcessingTime || '2-3 business days',
          approvalWorkflowStarted: result.approvalWorkflowStarted !== false,
          message: result.message || 'Request submitted successfully',
          data: result.data
        };
      } else {
        return {
          success: false,
          message: result.message || `Server error: ${response.status}`,
          data: result
        };
      }
    } catch (error: any) {
      return {
        success: false,
        message: error.message || 'Network error - please check your connection',
        data: { error: error.toString() }
      };
    }
  }, [getAccessToken]);

  // Submit gate pass request with file
// Fix the submitGatePassRequestWithFile function in useGatePassForm.ts
// Replace your existing function with this corrected version


// Enhanced submitGatePassRequestWithFile function with comprehensive error handling
// Replace the existing function in useGatePassForm.ts

const submitGatePassRequestWithFile = useCallback(async (
  requestData: GatePassRequest,
  file: File
): Promise<{
  success: boolean;
  requestId?: string;
  submissionDate?: string;
  estimatedProcessingTime?: string;
  approvalWorkflowStarted?: boolean;
  message?: string;
  data?: any;
}> => {
  try {
    console.group('🚀 ENHANCED FILE SUBMISSION DEBUG');
    console.log('📅 Timestamp:', new Date().toISOString());
    console.log('📝 Request Data:', JSON.stringify(requestData, null, 2));
    console.log('📎 File Info:', {
      name: file.name,
      size: file.size,
      type: file.type,
      lastModified: file.lastModified
    });
    
    const accessToken = await getAccessToken();
    console.log('✅ Access token obtained, length:', accessToken.length);
    
    // Create FormData with enhanced logging
    const uploadFormData = new FormData();
    
    // Add the file first
    uploadFormData.append('file', file);
    console.log('📎 File added to FormData');
    
    // Add each field individually with validation
    const fieldMappings = [
      { key: 'name', value: requestData.name, required: true },
      { key: 'email', value: requestData.email, required: true },
      { key: 'companyName', value: requestData.companyName, required: true },
      { key: 'consignmentDetails', value: requestData.consignmentDetails, required: false },
      { key: 'approverEmail', value: requestData.approverEmail, required: true },
      { key: 'idDetails', value: requestData.idDetails, required: false },
      { key: 'detailedDescription', value: requestData.detailedDescription, required: false },
      { key: 'approverUserId', value: requestData.approverUserId, required: false },
      { key: 'reviewerEmail', value: requestData.reviewerEmail, required: false },
      { key: 'reviewerUserId', value: requestData.reviewerUserId, required: false },
      { key: 'selectedFunctionId', value: requestData.selectedFunctionId?.toString(), required: false },
      { key: 'selectedCompanyId', value: requestData.selectedCompanyId?.toString(), required: false },
      { key: 'selectedReviewerFunctionId', value: requestData.selectedReviewerFunctionId?.toString(), required: false },
      { key: 'regionId', value: requestData.regionId?.toString(), required: false },
      { key: 'requestType', value: requestData.requestType, required: false },
      { key: 'passType', value: requestData.passType, required: false },
      { key: 'contractNumber', value: requestData.contractNumber, required: false },
      { key: 'startDate', value: requestData.startDate, required: false },
      { key: 'endDate', value: requestData.endDate, required: false },
      { key: 'passIdentificationName', value: requestData.passIdentificationName, required: false },
      { key: 'parentRequestId', value: requestData.parentRequestId?.toString(), required: false }
    ];

    console.group('📋 FORM DATA PREPARATION');
    fieldMappings.forEach(({ key, value, required }) => {
      if (value !== undefined && value !== null && value !== '') {
        uploadFormData.append(key, value);
        console.log(`✅ ${key}:`, value, `(${typeof value})`);
      } else if (required) {
        console.error(`❌ Missing required field: ${key}`);
        throw new Error(`Required field missing: ${key}`);
      } else {
        console.log(`⚪ Optional field ${key}: not provided`);
      }
    });
    
    // Add arrays as JSON strings with validation
    console.log('📊 Adding array fields...');
    
    if (!Array.isArray(requestData.gateIds) || requestData.gateIds.length === 0) {
      throw new Error('Gate IDs must be a non-empty array');
    }
    if (!Array.isArray(requestData.personnelIds) || requestData.personnelIds.length === 0) {
      throw new Error('Personnel IDs must be a non-empty array');
    }
    
    const gateIdsJson = JSON.stringify(requestData.gateIds);
    const personnelIdsJson = JSON.stringify(requestData.personnelIds);
    
    uploadFormData.append('gateIds', gateIdsJson);
    uploadFormData.append('personnelIds', personnelIdsJson);
    
    console.log('📊 gateIds JSON:', gateIdsJson);
    console.log('📊 personnelIds JSON:', personnelIdsJson);
    console.groupEnd(); // FORM DATA PREPARATION

    // Log all FormData entries for debugging
    console.group('📝 FINAL FORM DATA CONTENT');
    for (let [key, value] of uploadFormData.entries()) {
      if (value instanceof File) {
        console.log(`${key}: [File] ${value.name} (${value.size} bytes)`);
      } else {
        console.log(`${key}: ${value}`);
      }
    }
    console.groupEnd(); // FINAL FORM DATA CONTENT

    const apiUrl = `${API_BASE_URL}/gatepass_upload`;
    console.log('🌐 Making request to:', apiUrl);
    console.log('📋 Request headers will include Authorization');

    // Enhanced fetch with timeout and detailed error handling
    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      controller.abort();
      console.error('🕐 Request timed out after 60 seconds');
    }, 60000); // 60 second timeout

    console.log('📤 Sending request...');
    const startTime = Date.now();

    let response;
    try {
      response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`
          // Don't set Content-Type - let browser set it for FormData with boundary
        },
        body: uploadFormData,
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      const duration = Date.now() - startTime;
      console.log(`📥 Response received after ${duration}ms`);
      
    } catch (fetchError) {
      clearTimeout(timeoutId);
      console.error('🌐 Fetch error:', fetchError);
      
      if (fetchError === 'AbortError') {
        throw new Error('Request timed out - server may be overloaded');
      }
      
      throw new Error(`Network error: ${fetchError}`);
    }

    // Enhanced response analysis
    console.group('📊 RESPONSE ANALYSIS');
    console.log('📊 Response status:', response.status);
    console.log('📊 Response statusText:', response.statusText);
    console.log('📊 Response ok:', response.ok);
    console.log('📊 Response type:', response.type);
    console.log('📊 Response url:', response.url);
    console.log('📊 Response redirected:', response.redirected);
    
    // Log all response headers
    console.log('📊 Response headers:');
    for (let [key, value] of response.headers.entries()) {
      console.log(`  ${key}: ${value}`);
    }
    console.groupEnd(); // RESPONSE ANALYSIS

    // Enhanced response body handling
    console.group('📄 RESPONSE BODY PROCESSING');
    
    // Get raw response text first
    let responseText = '';
    try {
      console.log('📄 Reading response text...');
      responseText = await response.text();
      console.log('📄 Response text length:', responseText.length);
      
      if (responseText.length === 0) {
        console.error('❌ Response body is empty!');
        throw new Error('Server returned empty response');
      }
      
      // Log first and last 200 characters for debugging
      console.log('📄 Response text (first 200 chars):', responseText.substring(0, 200));
      if (responseText.length > 200) {
        console.log('📄 Response text (last 200 chars):', responseText.substring(responseText.length - 200));
      }
      
    } catch (textError) {
      console.error('❌ Failed to read response text:', textError);
      throw new Error(`Failed to read response: ${textError}`);
    }

    // Enhanced JSON parsing with detailed error handling
    let result;
    try {
      console.log('📄 Parsing JSON...');
      
      // Check if response looks like JSON
      const trimmedText = responseText.trim();
      if (!trimmedText.startsWith('{') && !trimmedText.startsWith('[')) {
        console.error('❌ Response does not look like JSON:', trimmedText.substring(0, 100));
        throw new Error(`Invalid response format - expected JSON but got: ${trimmedText.substring(0, 100)}`);
      }
      
      result = JSON.parse(responseText);
      console.log('✅ JSON parsed successfully');
      console.log('📄 Parsed result keys:', Object.keys(result));
      console.log('📄 Parsed result:', result);
      
    } catch (jsonError) {
      console.error('❌ JSON parsing failed:');
      console.error('❌ JSON error:', jsonError);
      console.error('❌ JSON error type:', jsonError);
      console.error('❌ Raw response that failed:', responseText);
      
      // Try to provide helpful error context
      if (responseText.includes('<!DOCTYPE html>')) {
        throw new Error('Server returned HTML instead of JSON - check server logs for errors');
      } else if (responseText.includes('Internal Server Error')) {
        throw new Error('Server internal error - check Azure Function logs');
      } else if (responseText.trim() === '') {
        throw new Error('Server returned empty response - possible timeout or crash');
      } else {
        throw new Error(`Invalid JSON response: ${jsonError}. Response: ${responseText.substring(0, 200)}`);
      }
    }
    
    console.groupEnd(); // RESPONSE BODY PROCESSING

    // Enhanced response validation
    console.group('✅ RESPONSE VALIDATION');
    
    if (response.ok) {
      console.log('✅ HTTP status indicates success');
      
      // Validate response structure
      if (typeof result !== 'object' || result === null) {
        throw new Error('Response is not a valid object');
      }
      
      if (result.success === false) {
        console.error('❌ Server reported failure:', result);
        throw new Error(result.message || result.error || 'Server reported failure');
      }
      
      console.log('✅ Response validation passed');
      
      const successResult = {
        success: true,
        requestId: result.requestId || result.data?.id || `REQ-${Date.now()}`,
        submissionDate: result.submissionDate || new Date().toISOString(),
        estimatedProcessingTime: result.estimatedProcessingTime || '2-3 business days',
        approvalWorkflowStarted: result.approvalWorkflowStarted !== false,
        message: result.message || 'Request with file submitted successfully',
        data: result.data
      };
      
      console.log('✅ Success result prepared:', successResult);
      console.groupEnd(); // RESPONSE VALIDATION
      console.groupEnd(); // ENHANCED FILE SUBMISSION DEBUG
      
      return successResult;
      
    } else {
      console.error('❌ HTTP status indicates failure:', response.status);
      console.error('❌ Server error response:', result);
      
      const errorResult = {
        success: false,
        message: result.message || result.error || `Server error: ${response.status} ${response.statusText}`,
        data: result
      };
      
      console.log('❌ Error result prepared:', errorResult);
      console.groupEnd(); // RESPONSE VALIDATION
      console.groupEnd(); // ENHANCED FILE SUBMISSION DEBUG
      
      return errorResult;
    }
    
  } catch (error: any) {
    console.group('💥 ERROR HANDLING');
    console.error('❌ Submission error details:');
    console.error('  Type:', error.constructor.name);
    console.error('  Message:', error.message);
    console.error('  Stack:', error.stack);
    
    // Enhanced error categorization
    let errorCategory = 'Unknown';
    if (error.name === 'AbortError') {
      errorCategory = 'Timeout';
    } else if (error.message.includes('fetch')) {
      errorCategory = 'Network';
    } else if (error.message.includes('JSON')) {
      errorCategory = 'JSON Parsing';
    } else if (error.message.includes('FormData')) {
      errorCategory = 'Form Data';
    } else if (error.message.includes('Authorization')) {
      errorCategory = 'Authentication';
    }
    
    console.error('  Category:', errorCategory);
    console.groupEnd(); // ERROR HANDLING
    console.groupEnd(); // ENHANCED FILE SUBMISSION DEBUG (if still open)
    
    return {
      success: false,
      message: error.message || 'Network error - please check your connection',
      data: { 
        error: error.toString(),
        type: error.constructor.name,
        category: errorCategory,
        stack: error.stack,
        timestamp: new Date().toISOString()
      }
    };
  }
}, [getAccessToken]);

useEffect(() => {
  const handleGlobalError = (event: ErrorEvent) => {
    console.error('🔥 GLOBAL ERROR CAUGHT:', {
      message: event.message,
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      error: event.error
    });
    
    if (event.message.includes('string did not match the expected pattern')) {
      console.error('🎯 PATTERN ERROR DETECTED IN GLOBAL HANDLER!');
      console.error('Location:', event.filename + ':' + event.lineno + ':' + event.colno);
    }
  };

  const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
    console.error('🔥 UNHANDLED PROMISE REJECTION:', event.reason);
    
    if (event.reason && event.reason.message && event.reason.message.includes('pattern')) {
      console.error('🎯 PATTERN ERROR IN PROMISE REJECTION!');
    }
  };

  window.addEventListener('error', handleGlobalError);
  window.addEventListener('unhandledrejection', handleUnhandledRejection);

  return () => {
    window.removeEventListener('error', handleGlobalError);
    window.removeEventListener('unhandledrejection', handleUnhandledRejection);
  };
}, []);

  // Initialize component
  useEffect(() => {
    const initialize = async () => {
      if (isInitializedRef.current) {
        return;
      }
      
      isInitializedRef.current = true;
      
      // Load regions first
      await fetchRegions();
      
      // Then load draft if needed
      if (initialDraftId) {
        await loadExistingDraft(initialDraftId);
      }
    };

    initialize();
  }, [fetchRegions, initialDraftId, loadExistingDraft]);

  // Auto-save
  const handleAutoSave = useCallback(async (): Promise<void> => {
    if (!currentDraftId || !formData || isSubmitting || isSavingDraft) {
      return;
    }
    
    try {
      const draftData = {
        draftId: currentDraftId,
        draftName: `Auto-saved - ${new Date().toLocaleTimeString()}`,
        formData: {
          ...formData,
          originalRequestId: originalRequestId || null,
          approverEmail: approverSelection?.selectedApprovers?.[0]?.userEmail,
          approverUserId: approverSelection?.selectedApprovers?.[0]?.userId?.toString(),
          approverNames: approverSelection?.selectedApprovers?.map(a => a.userName).join(','),
          approverFunctionName: approverSelection?.function?.functionName || '',
          reviewerEmail: reviewerSelection?.selectedReviewers?.[0]?.userEmail,
          reviewerUserId: reviewerSelection?.selectedReviewers?.[0]?.userId?.toString(),
          reviewerNames: reviewerSelection?.selectedReviewers?.map(r => r.userName).join(','),
          reviewerCompanyName: reviewerSelection?.company?.companyName || '',
          reviewerFunctionName: reviewerSelection?.function?.functionName || '',
          selectedFunctionId: approverSelection?.function?.functionId,
          selectedCompanyId: reviewerSelection?.company?.companyId || userInfo.companyId,
          selectedReviewerFunctionId: reviewerSelection?.function?.functionId
        },
        gateIds: selectedGates.map(gate => gate.gateId),
        personnelIds: selectedPersonnel.map(person => person.personnelId)
      };

      await saveDraft(draftData, getAccessToken);
      setLastSavedAt(new Date());
    } catch (error) {
      console.warn('Auto-save failed:', error);
    }
  }, [
    currentDraftId, formData, approverSelection, reviewerSelection, 
    selectedGates, selectedPersonnel, getAccessToken, userInfo, isSubmitting, isSavingDraft,
    originalRequestId
  ]);

  // Auto-save interval
  useEffect(() => {
    if (!currentDraftId) return;

    const autoSaveInterval = setInterval(() => {
      handleAutoSave();
    }, AUTO_SAVE_INTERVAL_MS);

    return () => clearInterval(autoSaveInterval);
  }, [currentDraftId, handleAutoSave]);

  // Recalculate personnel visa issues whenever selection changes
  useEffect(() => {
    const issues = getPersonnelWithVisaIssues(selectedPersonnel);
    setPersonnelVisaIssues(issues);
    if (issues.length > 0) {
      console.warn('⚠️ Personnel with visa issues detected:', issues.map(p => ({
        name: p.nationalIdNo,
        nationality: p.nationality,
        visaExpiry: p.visaExpiry
      })));
    }
  }, [selectedPersonnel]);

  // ============================================
  // VALIDATION FUNCTIONS
  // ============================================

  const validateStep = useCallback((step: number): boolean => {
    if (!formData) return false;
    
    switch (step) {
      case 1: // Basic Information
        return !!(
          formData.name && 
          formData.email && 
          formData.companyName && 
          formData.regionId && 
          formData.requestType && 
          formData.passType && 
          formData.contractNumber?.trim() && 
          formData.startDate && 
          formData.endDate && 
          validateDateRange(formData.startDate, formData.endDate)
        );
      case 2: // Gate Selection
        return selectedGates.length > 0;
      case 3: // Personnel
        return selectedPersonnel.length > 0;
      case 4: // Approvers
        return !!(approverSelection && approverSelection.selectedApprovers.length > 0);
      case 5: // Reviewers (optional)
        return true;
      case 6: // Request Details
        return true;
      default:
        return true;
    }
  }, [formData, selectedGates, selectedPersonnel, approverSelection]);

  // ============================================
  // NAVIGATION FUNCTIONS
  // ============================================

  const goToNextStep = useCallback(() => {
    if (currentStep < STEPS.length) {
      setCurrentStep(currentStep + 1);
    }
  }, [currentStep]);

  const goToPrevStep = useCallback(() => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  }, [currentStep]);

  const goToStep = useCallback((step: number) => {
    if (step >= 1 && step <= STEPS.length) {
      setCurrentStep(step);
    }
  }, []);

  const canProceedToNext = useCallback((): boolean => {
    return validateStep(currentStep);
  }, [validateStep, currentStep]);

  const getStepStatus = useCallback((step: number): 'completed' | 'current' | 'pending' | 'invalid' => {
    if (step < currentStep) {
      return validateStep(step) ? 'completed' : 'invalid';
    } else if (step === currentStep) {
      return 'current';
    } else {
      return 'pending';
    }
  }, [currentStep, validateStep]);

  // ============================================
  // EVENT HANDLERS
  // ============================================

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>): void => {
    const { name, value } = e.target;
    
    if (name === 'regionId') {
      setFormData(prev => ({
        ...prev,
        [name]: value ? parseInt(value) : null
      }));
    } else if (name === 'endDate') {
      // Validate end date against start date immediately
      setFormData(prev => {
        if (prev.startDate && value && !validateDateRange(prev.startDate, value)) {
          // Show error via ErrorMessageBanner and clear the invalid end date
          onStatusUpdate({
            type: 'error',
            message: 'End date must be on or after start date. Please select a valid end date.'
          });
          return { ...prev, endDate: '' };
        }
        return { ...prev, endDate: value };
      });
    } else if (name === 'startDate') {
      // If start date moves past the existing end date, clear end date and warn
      setFormData(prev => {
        if (prev.endDate && value && !validateDateRange(value, prev.endDate)) {
          onStatusUpdate({
            type: 'error',
            message: 'Start date is now after the end date. End date has been cleared — please re-select.'
          });
          return { ...prev, startDate: value, endDate: '' };
        }
        return { ...prev, startDate: value };
      });
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
    
    // Clear any existing status message (except when we just set one for date validation above)
    if (name !== 'endDate' && name !== 'startDate') {
      onStatusUpdate({ type: null, message: '' });
    }
  }, [onStatusUpdate]);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    setFileError('');
    if (file) {
      setSelectedFile(file);
    } else {
      setSelectedFile(null);
    }
  }, []);

  const handleRemoveGate = useCallback((gateId: number) => {
    setSelectedGates(prev => prev.filter(g => g.gateId !== gateId));
  }, []);

  const handleRemovePersonnel = useCallback((personnelId: number) => {
    setSelectedPersonnel(prev => prev.filter(p => p.personnelId !== personnelId));
  }, []);

  // Save draft handler
  const handleSaveDraft = useCallback(async (draftName: string): Promise<void> => {
    try {
      setIsSavingDraft(true);
      
      if (!formData) {
        throw new Error('Form data is not available');
      }
      
      const draftData = {
        draftId: currentDraftId,
        draftName,
        formData: {
          ...formData,
          originalRequestId: originalRequestId || null,
          approverEmail: approverSelection?.selectedApprovers?.[0]?.userEmail,
          approverUserId: approverSelection?.selectedApprovers?.[0]?.userId?.toString(),
          approverNames: approverSelection?.selectedApprovers?.map(a => a.userName).join(','),
          approverFunctionName: approverSelection?.function?.functionName || '',
          reviewerEmail: reviewerSelection?.selectedReviewers?.[0]?.userEmail,
          reviewerUserId: reviewerSelection?.selectedReviewers?.[0]?.userId?.toString(),
          reviewerNames: reviewerSelection?.selectedReviewers?.map(r => r.userName).join(','),
          reviewerCompanyName: reviewerSelection?.company?.companyName || '',
          reviewerFunctionName: reviewerSelection?.function?.functionName || '',
          selectedFunctionId: approverSelection?.function?.functionId,
          selectedCompanyId: reviewerSelection?.company?.companyId || userInfo.companyId,
          selectedReviewerFunctionId: reviewerSelection?.function?.functionId
        },
        gateIds: selectedGates.map(gate => gate.gateId),
        personnelIds: selectedPersonnel.map(person => person.personnelId)
      };

      const result = await saveDraft(draftData, getAccessToken);
      
      if (result.success) {
        setCurrentDraftId(result.data.draftId);
        setLastSavedAt(new Date());
        setShowSaveDraftDialog(false);
        
        onStatusUpdate({
          type: 'success',
          message: `Draft "${draftName}" saved successfully!`
        });
        
        if (onDraftSaved) {
          onDraftSaved(result.data.draftId);
        }
      }
    } catch (error) {
      onStatusUpdate({
        type: 'error',
        message: `Failed to save draft: ${error instanceof Error ? error.message : 'Unknown error occurred'}`
      });
    } finally {
      setIsSavingDraft(false);
    }
  }, [
    currentDraftId, formData, approverSelection, reviewerSelection, 
    selectedGates, selectedPersonnel, getAccessToken, userInfo, 
    onStatusUpdate, onDraftSaved, originalRequestId
  ]);



  // Submit handler
 const handleSubmit = useCallback(async (): Promise<{
  success: boolean;
  requestId?: string;
  submissionDate?: string;
  estimatedProcessingTime?: string;
  approvalWorkflowStarted?: boolean;
  message?: string;
  errorDetails?: string;
}> => {
  console.group('🚀 GATE PASS SUBMISSION DEBUG - START');
  console.log('⏰ Submission started at:', new Date().toISOString());
  
  try {
    // ============================================
    // PRE-VALIDATION LOGGING
    // ============================================
    console.group('🔍 PRE-VALIDATION DATA INSPECTION');
    
    console.log('📋 Form Data:', {
      name: formData.name,
      email: formData.email,
      companyName: formData.companyName,
      regionId: formData.regionId,
      regionIdType: typeof formData.regionId,
      requestType: formData.requestType,
      passType: formData.passType,
      startDate: formData.startDate,
      endDate: formData.endDate,
      consignmentDetails: formData.consignmentDetails,
      detailedDescription: formData.detailedDescription,
      contractNumber: formData.contractNumber,
      passIdentificationName: formData.passIdentificationName,
      idDetails: formData.idDetails,
      parentRequestId: formData.parentRequestId
    });
    
    console.log('🚪 Selected Gates:', selectedGates.map(gate => ({
      gateId: gate.gateId,
      gateNumber: gate.gateNumber,
      locationName: gate.locationName,
      regionId: gate.regionId,
      isActive: gate.isActive
    })));
    
    console.log('👥 Selected Personnel:', selectedPersonnel.map(person => ({
      personnelId: person.personnelId,
      nationalIdNo: person.nationalIdNo,
      nationality: person.nationality,
      dateOfBirth: person.dateOfBirth,
      visaExpiry: person.visaExpiry
    })));
    
    console.log('✅ Approver Selection:', {
      hasSelection: !!approverSelection,
      function: approverSelection?.function,
      approversCount: approverSelection?.selectedApprovers?.length || 0,
      approvers: approverSelection?.selectedApprovers?.map(a => ({
        userId: a.userId,
        userName: a.userName,
        userEmail: a.userEmail
      }))
    });
    
    console.log('👀 Reviewer Selection:', {
      hasSelection: !!reviewerSelection,
      company: reviewerSelection?.company,
      function: reviewerSelection?.function,
      reviewersCount: reviewerSelection?.selectedReviewers?.length || 0,
      reviewers: reviewerSelection?.selectedReviewers?.map(r => ({
        userId: r.userId,
        userName: r.userName,
        userEmail: r.userEmail
      }))
    });
    
    console.log('📎 File Info:', {
      hasFile: !!selectedFile,
      fileName: selectedFile?.name,
      fileSize: selectedFile?.size,
      fileType: selectedFile?.type
    });
    
    console.groupEnd(); // PRE-VALIDATION DATA INSPECTION
    
    // ============================================
    // FIELD-BY-FIELD VALIDATION WITH LOGGING
    // ============================================
    console.group('🔍 DETAILED FIELD VALIDATION');
    
    // Email validation with detailed logging
    console.log('📧 Email Validation:');
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const isEmailValid = emailPattern.test(formData.email);
    console.log(`  - Email: "${formData.email}"`);
    console.log(`  - Pattern: ${emailPattern}`);
    console.log(`  - Is Valid: ${isEmailValid}`);
    
    if (!isEmailValid) {
      console.error('❌ EMAIL VALIDATION FAILED!');
      const error = { 
        success: false, 
        message: 'Invalid email format',
        errorDetails: `Email "${formData.email}" failed pattern validation`
      };
      onStatusUpdate({ type: 'error', message: error.message });
      console.groupEnd(); // DETAILED FIELD VALIDATION
      console.groupEnd(); // GATE PASS SUBMISSION DEBUG
      return error;
    }
    
    // Name validation with character check
    console.log('👤 Name Validation:');
    const nameSpecialChars = /[<>"'&]/.test(formData.name || '');
    console.log(`  - Name: "${formData.name}"`);
    console.log(`  - Has special characters: ${nameSpecialChars}`);
    console.log(`  - Length: ${formData.name?.length || 0}`);
    
    // Region ID validation
    console.log('🌍 Region ID Validation:');
    console.log(`  - Region ID: ${formData.regionId}`);
    console.log(`  - Type: ${typeof formData.regionId}`);
    console.log(`  - Is falsy: ${!formData.regionId}`);
    
    // Date validation with detailed logging
    console.log('📅 Date Validation:');
    console.log(`  - Start Date: ${formData.startDate} (${typeof formData.startDate})`);
    console.log(`  - End Date: ${formData.endDate} (${typeof formData.endDate})`);
    
    if (formData.startDate && formData.endDate) {
      const startDate = new Date(formData.startDate);
      const endDate = new Date(formData.endDate);
      console.log(`  - Start Date parsed: ${startDate.toISOString()}`);
      console.log(`  - End Date parsed: ${endDate.toISOString()}`);
      console.log(`  - End >= Start: ${endDate >= startDate}`);
    }
    
    // Personnel validation
    console.log('👥 Personnel ID Validation:');
    selectedPersonnel.forEach((person, index) => {
      const hasSpecialChars = /[^a-zA-Z0-9-]/.test(person.nationalIdNo || '');

      console.log(`  - Personnel ${index + 1}:`);
      console.log(`    - ID: "${person.nationalIdNo}"`);
      console.log(`    - Has special chars: ${hasSpecialChars}`);
      console.log(`    - Personnel ID: ${person.personnelId} (${typeof person.personnelId})`);
    });
    
    console.groupEnd(); // DETAILED FIELD VALIDATION
    
    // ============================================
    // EXISTING VALIDATION CHECKS
    // ============================================
    console.group('🔍 BUSINESS LOGIC VALIDATION');
    
    if (!validateDateRange(formData.startDate, formData.endDate)) {
      console.error('❌ Date range validation failed');
      const error = { 
        success: false, 
        message: 'End date must be on or after start date.',
        errorDetails: `Start: ${formData.startDate}, End: ${formData.endDate}`
      };
      onStatusUpdate({ type: 'error', message: error.message });
      console.groupEnd();
      console.groupEnd();
      return error;
    }
    console.log('✅ Date range validation passed');
    
    if (selectedGates.length === 0) {
      console.error('❌ No gates selected');
      const error = { 
        success: false, 
        message: 'Please select at least one gate.',
        errorDetails: 'No gates selected in form validation'
      };
      onStatusUpdate({ type: 'error', message: error.message });
      console.groupEnd();
      console.groupEnd();
      return error;
    }
    console.log(`✅ Gates validation passed (${selectedGates.length} gates)`);
    
    if (selectedPersonnel.length === 0) {
      console.error('❌ No personnel selected');
      const error = { 
        success: false, 
        message: 'Please select at least one personnel for the gate pass request.',
        errorDetails: 'No personnel selected in form validation'
      };
      onStatusUpdate({ type: 'error', message: error.message });
      console.groupEnd();
      console.groupEnd();
      return error;
    }
    console.log(`✅ Personnel validation passed (${selectedPersonnel.length} personnel)`);
    
    if (!approverSelection || !approverSelection.selectedApprovers || approverSelection.selectedApprovers.length === 0) {
      console.error('❌ No approvers selected');
      const error = { 
        success: false, 
        message: 'Please select at least one approver for the gate pass request.',
        errorDetails: 'No approvers selected in form validation'
      };
      onStatusUpdate({ type: 'error', message: error.message });
      console.groupEnd();
      console.groupEnd();
      return error;
    }
    console.log(`✅ Approvers validation passed (${approverSelection.selectedApprovers.length} approvers)`);
    
    if (!formData.regionId) {
      console.error('❌ No region selected');
      const error = { 
        success: false, 
        message: 'Please select a region.',
        errorDetails: 'No region selected in form validation'
      };
      onStatusUpdate({ type: 'error', message: error.message });
      console.groupEnd();
      console.groupEnd();
      return error;
    }
    console.log(`✅ Region validation passed (Region: ${formData.regionId})`);

    console.log('ℹ️ Consignment details validation skipped (optional)');

    // Validate basic info fields required by backend completionStatus
    if (!formData.name || formData.name.trim() === '') {
      console.error('❌ Name is missing');
      const error = { 
        success: false, 
        message: 'Please provide your name.',
        errorDetails: 'Name is required but missing'
      };
      onStatusUpdate({ type: 'error', message: error.message });
      console.groupEnd();
      console.groupEnd();
      return error;
    }
    console.log('✅ Name validation passed');

    if (!formData.companyName || formData.companyName.trim() === '') {
      console.error('❌ Company name is missing');
      const error = { 
        success: false, 
        message: 'Please provide your company name.',
        errorDetails: 'Company name is required but missing'
      };
      onStatusUpdate({ type: 'error', message: error.message });
      console.groupEnd();
      console.groupEnd();
      return error;
    }
    console.log('✅ Company name validation passed');
    
    console.groupEnd(); // BUSINESS LOGIC VALIDATION
    
    // ============================================
    // SANITIZE DATA BEFORE SUBMISSION
    // ============================================
    console.group('🧹 DATA SANITIZATION');
    
    // Sanitize text fields
    const sanitizedName = formData.name?.replace(/[<>"'&]/g, '').trim();
    const sanitizedConsignmentDetails = formData.consignmentDetails?.replace(/[<>"'&]/g, '').trim();
    
    console.log('Before sanitization:', {
      name: formData.name,
      consignmentDetails: formData.consignmentDetails
    });
    
    console.log('After sanitization:', {
      name: sanitizedName,
      consignmentDetails: sanitizedConsignmentDetails
    });
    
    console.groupEnd(); // DATA SANITIZATION
    
    // === DOCUMENT VALIDATION CHECK (HARD BLOCK) ===
    console.group('🔍 DOCUMENT VALIDATION CHECK');
    const personnelWithVisaIssues = getPersonnelWithVisaIssues(selectedPersonnel);
    console.log('Personnel with document issues:', personnelWithVisaIssues);
    
    if (personnelWithVisaIssues.length > 0) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const issueList = personnelWithVisaIssues.map(p => {
        const personAny = p as any;
        const passportExpiry = personAny.passportExpiryDate || personAny.passportExpiry;
        const reasons: string[] = [];

        if (passportExpiry && new Date(passportExpiry) < today) {
          reasons.push(`passport expired on ${new Date(passportExpiry).toLocaleDateString()}`);
        }
        if (!isQatariNational(p.nationality)) {
          if (!p.visaExpiry) {
            reasons.push('no visa on record');
          } else if (new Date(p.visaExpiry) < today) {
            reasons.push(`visa expired on ${new Date(p.visaExpiry).toLocaleDateString()}`);
          }
        }

        return `${p.nationalIdNo} (${p.nationality || 'Unknown'} - ${reasons.join(', ')})`;
      }).join(', ');
      
      console.error('❌ Submission blocked due to document issues:', issueList);
      const error = { 
        success: false, 
        message: `Cannot submit: The following personnel have expired documents: ${issueList}. Please remove them or update their information before proceeding.`,
        errorDetails: `Personnel with document issues: ${issueList}`
      };
      onStatusUpdate({ type: 'error', message: error.message });
      console.groupEnd(); // DOCUMENT VALIDATION CHECK
      console.groupEnd(); // BUSINESS LOGIC VALIDATION  
      console.groupEnd(); // GATE PASS SUBMISSION DEBUG
      return error;
    } else {
      console.log('✅ All personnel have valid documents');
    }
    console.groupEnd(); // DOCUMENT VALIDATION CHECK

    // === START SUBMISSION PROCESS ===
    console.group('📤 SUBMISSION PROCESS');
    setIsSubmitting(true);
    onStatusUpdate({ type: null, message: 'Submitting your gate pass request...' });

    // ═══════════════════════════════════════════════════════
    // REVISION DRAFT RESUBMISSION PATH
    // If this is a revision draft, save the draft with latest data
    // then submit via submitDraft API to preserve original request number.
    // ═══════════════════════════════════════════════════════
    if (originalRequestId && currentDraftId) {
      try {
        console.log(`🔄 Resubmitting revision draft #${currentDraftId} for original request #${originalRequestId}`);
        onStatusUpdate({ type: null, message: 'Saving and resubmitting your revised request...' });

        // Save latest form state to the draft first
        const draftData = {
          draftId: currentDraftId,
          draftName: `Revision - Request #${originalRequestId}`,
          formData: {
            ...formData,
            originalRequestId: originalRequestId,
            approverEmail: approverSelection?.selectedApprovers?.map(a => a.userEmail).join(','),
            approverUserId: approverSelection?.selectedApprovers?.map(a => a.userId.toString()).join(','),
            approverNames: approverSelection?.selectedApprovers?.map(a => a.userName).join(','),
            approverFunctionName: approverSelection?.function?.functionName || '',
            reviewerEmail: reviewerSelection?.selectedReviewers?.length 
              ? reviewerSelection.selectedReviewers.map(r => r.userEmail).join(',') 
              : undefined,
            reviewerUserId: reviewerSelection?.selectedReviewers?.length 
              ? reviewerSelection.selectedReviewers.map(r => r.userId.toString()).join(',') 
              : undefined,
            reviewerNames: reviewerSelection?.selectedReviewers?.length
              ? reviewerSelection.selectedReviewers.map(r => r.userName).join(',')
              : undefined,
            reviewerCompanyName: reviewerSelection?.company?.companyName || '',
            reviewerFunctionName: reviewerSelection?.function?.functionName || '',
            selectedFunctionId: approverSelection?.function?.functionId,
            selectedCompanyId: reviewerSelection?.company?.companyId || userInfo.companyId,
            selectedReviewerFunctionId: reviewerSelection?.function?.functionId
          },
          gateIds: selectedGates.map(gate => gate.gateId),
          personnelIds: selectedPersonnel.map(person => person.personnelId)
        };

        await saveDraft(draftData, getAccessToken);
        console.log('✅ Draft saved with latest changes');

        // Submit via the draft submit API (handles UPDATE vs INSERT on backend)
        const submitResult = await submitDraftToApi(currentDraftId, getAccessToken);
        console.log('✅ Revision submitted:', submitResult);

        if (submitResult.success) {
          const submissionData: SubmissionSuccessData = {
            requestId: submitResult.data?.requestId?.toString() || originalRequestId.toString(),
            status: 'Pending',
            region: getRegionName(formData.regionId, regions),
            submissionDate: generateSubmissionDateString(),
            gates: selectedGates.map(g => g.gateNumber),
            personnel: selectedPersonnel.map(p => p.nationalIdNo),
            approvers: approverSelection?.selectedApprovers?.map(a => a.userName) || [],
            reviewers: reviewerSelection?.selectedReviewers?.map(r => r.userName) || [],
            estimatedProcessingTime: '2-3 business days',
            emailSent: true,
            fileUploaded: false
          };

          onSubmissionSuccess(submissionData);

          // Reset form state
          setFormData({ ...createDefaultFormData(userInfo), passType: '' as FormData['passType'], startDate: '', endDate: '' });
          setSelectedFile(null);
          setSelectedGates([]);
          setSelectedPersonnel([]);
          setApproverSelection(null);
          setReviewerSelection(null);
          setFileError('');
          setCurrentDraftId(null);
          setOriginalRequestId(null);
          setLastSavedAt(null);

          console.groupEnd(); // SUBMISSION PROCESS
          console.groupEnd(); // GATE PASS SUBMISSION DEBUG

          return {
            success: true,
            requestId: submitResult.data?.requestId || originalRequestId,
            submissionDate: new Date().toISOString(),
            estimatedProcessingTime: '2-3 business days',
            approvalWorkflowStarted: true,
            message: `Request #${submitResult.data?.requestId || originalRequestId} resubmitted successfully after revision`
          };
        } else {
          throw new Error(submitResult.message || 'Failed to resubmit revised request');
        }
      } catch (revisionError) {
        console.error('❌ Revision resubmission failed:', revisionError);
        const errorMsg = revisionError instanceof Error ? revisionError.message : 'Failed to resubmit';
        onStatusUpdate({ type: 'error', message: errorMsg });
        console.groupEnd(); // SUBMISSION PROCESS
        console.groupEnd(); // GATE PASS SUBMISSION DEBUG
        return { success: false, message: errorMsg };
      } finally {
        setIsSubmitting(false);
      }
    }

    // ═══════════════════════════════════════════════════════
    // NORMAL SUBMISSION PATH (new request)
    // ═══════════════════════════════════════════════════════

    // === PREPARE REQUEST DATA WITH DETAILED LOGGING ===
    console.group('📋 REQUEST DATA PREPARATION');
    
    const requestData: GatePassRequest = {
      name: sanitizedName || formData.name,
      email: formData.email,
      companyName: formData.companyName,
      idDetails: formData.idDetails,
      consignmentDetails: sanitizedConsignmentDetails || formData.consignmentDetails,
      detailedDescription: formData.detailedDescription || undefined,
      approverEmail: approverSelection.selectedApprovers.map(a => a.userEmail).join(','),
      approverUserId: approverSelection.selectedApprovers.map(a => a.userId.toString()).join(','),
      reviewerEmail: reviewerSelection?.selectedReviewers?.length 
        ? reviewerSelection.selectedReviewers.map(r => r.userEmail).join(',') 
        : undefined,
      reviewerUserId: reviewerSelection?.selectedReviewers?.length 
        ? reviewerSelection.selectedReviewers.map(r => r.userId.toString()).join(',') 
        : undefined,
      selectedFunctionId: approverSelection.function?.functionId,
      selectedCompanyId: reviewerSelection?.company?.companyId || userInfo.companyId,
      selectedReviewerFunctionId: reviewerSelection?.function?.functionId,
      gateIds: selectedGates.map(gate => gate.gateId),
      personnelIds: selectedPersonnel.map(person => person.personnelId),
      // Include the full approver/reviewer arrays for direct task creation
      selectedApprovers: approverSelection.selectedApprovers.map(a => ({
        userId: a.userId,
        userName: a.userName,
        userEmail: a.userEmail
      })),
      selectedReviewers: reviewerSelection?.selectedReviewers?.map(r => ({
        userId: r.userId,
        userName: r.userName,
        userEmail: r.userEmail
      })) || [],
      regionId: formData.regionId,
      requestType: formData.requestType,
      passType: formData.passType,
      contractNumber: formData.contractNumber,
      startDate: formData.startDate,
      endDate: formData.endDate,
      passIdentificationName: formData.passIdentificationName,
      parentRequestId: formData.parentRequestId || null
    };

    console.log('🗃️ Final Request Data Object:', JSON.stringify(requestData, null, 2));
    
    // Check for any null/undefined critical fields
    console.log('🔍 Critical Field Check:');
    const criticalFields = ['name', 'email', 'regionId', 'approverEmail'];
    criticalFields.forEach(field => {
      const value = requestData[field as keyof GatePassRequest];
      console.log(`  - ${field}: ${value} (${typeof value}) - ${value ? '✅' : '❌'}`);
    });
    
    console.log('🔍 Array Field Check:');
    console.log(`  - gateIds: [${requestData.gateIds.join(', ')}] (length: ${requestData.gateIds.length})`);
    console.log(`  - personnelIds: [${requestData.personnelIds.join(', ')}] (length: ${requestData.personnelIds.length})`);
    
    console.groupEnd(); // REQUEST DATA PREPARATION

    // === ACTUAL API SUBMISSION ===
    console.group('🌐 API SUBMISSION');
    let response;
    
    if (selectedFile) {
      console.log('📎 Submitting with file:', {
        fileName: selectedFile.name,
        fileSize: selectedFile.size,
        fileType: selectedFile.type
      });
      response = await submitGatePassRequestWithFile(requestData, selectedFile);
    } else {
      console.log('📄 Submitting without file');
      response = await submitGatePassRequest(requestData);
    }
    
    console.log('📥 API Response:', response);
    console.groupEnd(); // API SUBMISSION

    // === HANDLE API RESPONSE ===
    console.group('📊 RESPONSE HANDLING');
    
    if (response && response.success) {
      console.log('✅ Submission successful!');
      
      // Prepare success data for the original callback
      const submissionData: SubmissionSuccessData = {
        requestId: response.requestId || response.data?.id || `REQ-${Date.now()}`,
        status: 'Pending',
        region: getRegionName(formData.regionId, regions),
        submissionDate: response.submissionDate || generateSubmissionDateString(),
        gates: selectedGates.map(g => g.gateNumber),
        personnel: selectedPersonnel.map(p => p.nationalIdNo),
        approvers: approverSelection.selectedApprovers.map(a => a.userName),
        reviewers: reviewerSelection?.selectedReviewers?.map(r => r.userName) || [],
        estimatedProcessingTime: response.estimatedProcessingTime || PROCESSING_TIME,
        emailSent: true,
        fileUploaded: !!selectedFile
      };
      
      console.log('🎉 Success Data:', submissionData);
      
      // Call the original success callback
      onSubmissionSuccess(submissionData);
      
      // Reset form state
      console.log('🔄 Resetting form state...');
      setFormData({ ...createDefaultFormData(userInfo), passType: '' as FormData['passType'], startDate: '', endDate: '' });
      setSelectedFile(null);
      setSelectedGates([]);
      setSelectedPersonnel([]);
      setApproverSelection(null);
      setReviewerSelection(null);
      setFileError('');
      setCurrentDraftId(null);
      setOriginalRequestId(null);
      setLastSavedAt(null);
      
      console.groupEnd(); // RESPONSE HANDLING
      console.groupEnd(); // SUBMISSION PROCESS
      console.groupEnd(); // GATE PASS SUBMISSION DEBUG
      
      // Return success result
      return {
        success: true,
        requestId: response.requestId || response.data?.id,
        submissionDate: response.submissionDate || new Date().toISOString(),
        estimatedProcessingTime: response.estimatedProcessingTime || PROCESSING_TIME,
        approvalWorkflowStarted: response.approvalWorkflowStarted !== false,
        message: response.message || 'Gate pass request submitted successfully!'
      };
      
    } else {
      console.error('❌ Submission failed');
      console.error('Response:', response);
      throw new Error(response?.message || 'Submission failed - please try again');
    }
    
  } catch (error: unknown) {
    console.group('💥 ERROR HANDLING');
    console.error('❌ Submission error occurred:', error);
    
    // Log error details
    if (error instanceof Error) {
      console.error('Error name:', error.name);
      console.error('Error message:', error.message);
      console.error('Error stack:', error.stack);
    } else {
      console.error('Non-Error object thrown:', error);
    }
    
    // Check if it's a network error
    if (error instanceof TypeError && error.message.includes('fetch')) {
      console.error('🌐 Network error detected');
    }
    
    // Check if it's a JSON parsing error
    if (error instanceof SyntaxError && error.message.includes('JSON')) {
      console.error('📄 JSON parsing error detected');
    }
    
    const userFriendlyMessage = createErrorMessage(error);
    console.error('User-friendly message:', userFriendlyMessage);
    
    onStatusUpdate({
      type: 'error',
      message: userFriendlyMessage
    });
    
    console.groupEnd(); // ERROR HANDLING
    console.groupEnd(); // GATE PASS SUBMISSION DEBUG (if still open)
    
    return {
      success: false,
      message: userFriendlyMessage,
      errorDetails: error instanceof Error ? 
        (error.stack || error.toString()) : 
        JSON.stringify(error, null, 2)
    };
    
  } finally {
    console.log('🏁 Submission process completed');
    setIsSubmitting(false);
    console.groupEnd(); // Ensure any remaining groups are closed
  }
}, [
  formData, selectedGates, selectedPersonnel, approverSelection, 
  reviewerSelection, selectedFile, userInfo, regions, onStatusUpdate, 
  onSubmissionSuccess, submitGatePassRequest, submitGatePassRequestWithFile,
  originalRequestId, currentDraftId, getAccessToken
]);

  return {
    // State
    currentStep,
    formData,
    regions,
    loadingRegions,
    regionError,
    selectedGates,
    selectedPersonnel,
    personnelVisaIssues,
    approverSelection,
    reviewerSelection,
    selectedFile,
    fileError,
    isSubmitting,
    isLoadingDraft,
    draftLoadError,
    showSaveDraftDialog,
    isSavingDraft,
    lastSavedAt,
    originalRequestId,
    loadedParentRequest,
    
    // Popup states
    showGateSelection,
    showPersonnelSelection,
    showApproverSelection,
    showReviewerSelection,
    
    // Actions
    setShowGateSelection,
    setShowPersonnelSelection,
    setShowApproverSelection,
    setShowReviewerSelection,
    setSelectedGates: (gates: Gate[]) => setSelectedGates(gates.map(adaptGateForForm)),
    setSelectedPersonnel,
    setApproverSelection,
    setReviewerSelection,
    setShowSaveDraftDialog,
    
    // Handlers
    handleInputChange,
    handleFileChange,
    handleRemoveGate,
    handleRemovePersonnel,
    handleSaveDraft,
    handleSubmit,
    
    // Navigation
    goToNextStep,
    goToPrevStep,
    goToStep,
    canProceedToNext,
    getStepStatus,
    
    // Utils
    validateStep,
    fetchRegions
  };
};